import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  isMobile,
  isTablet,
  isIos,
  HealthMonitorCodes,
  SessionState,
  Sex,
  SmokingStatus,
} from '@biosensesignal/web-sdk';
import { useMediaPredicate } from 'react-media-hook';
import {
  useError,
  useLicenseKey,
  useMeasurementDuration,
  useMonitor,
  usePageVisibility,
  usePrevious,
  useWarning,
} from '../hooks';
import Stats from './Stats';
import StartButton from './StartButton';
import { mirror } from '../style/mirror';
import { Flex } from './shared/Flex';
import Timer from './Timer';
import media from '../style/media';
import InfoBar from './InfoBar';
import { ErrorAlert, InfoAlert, WarningAlert } from './alert';
import Loader from './Loader';
import { VideoReadyState } from '../types';
import TopBar from './TopBar';
import Mask from '../assets/mask.svg';
import MaskGreen from '../assets/mask-green.svg';
import MaskRed from '../assets/mask-red.svg';
import { StorageKeys } from '../utils/storage';
import { 
  useUpdateLicenseUsageSessionMutation, 
  useDecodeVitalByPatientIdMutation,
  useLazyGetWebhookPatientDataQuery,
  useSendWebhookMutation
} from '../services/patientApi';
import { WEBHOOK_URL, API_BASE_URL } from '../constants/api';
import { BIOSENSE_ERROR_MAP } from '../utils/biosenseErrorCodes';


const MonitorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  gap: 2rem;
  z-index: 10;
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;


const slideTelemetry = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-100%); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.2), inset 0 0 40px rgba(0, 0, 0, 0.8); border-color: rgba(16, 185, 129, 0.3); }
  50% { box-shadow: 0 0 60px rgba(16, 185, 129, 0.4), inset 0 0 60px rgba(0, 0, 0, 0.6); border-color: rgba(16, 185, 129, 0.8); }
  100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.2), inset 0 0 40px rgba(0, 0, 0, 0.8); border-color: rgba(16, 185, 129, 0.3); }
`;

const scanAnimation = keyframes`
  0% { top: 10%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { top: 90%; opacity: 0; }
`;


const VideoAndStatsWrapper = styled.div<{ isMeasuring: boolean }>`
  position: relative;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 4/3;
  border-radius: 48px;
  overflow: hidden;
  background: #000;
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${({ isMeasuring }) => isMeasuring && css`
    animation: ${pulseGlow} 3s infinite ease-in-out;
    border-width: 2px;
  `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, transparent 40%, rgba(0, 0, 0, 0.6) 100%);
    pointer-events: none;
    z-index: 4;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ScanlineOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    rgba(18, 16, 16, 0) 50%,
    rgba(0, 0, 0, 0.1) 50%
  ),
  linear-gradient(
    90deg,
    rgba(255, 0, 0, 0.03),
    rgba(0, 255, 0, 0.01),
    rgba(0, 0, 255, 0.03)
  );
  background-size: 100% 4px, 3px 100%;
  pointer-events: none;
  z-index: 5;
`;

const noise = keyframes`
  0% { transform: translate(0,0) }
  10% { transform: translate(-5%,-5%) }
  20% { transform: translate(-10%,5%) }
  30% { transform: translate(5%,-10%) }
  40% { transform: translate(-5%,15%) }
  50% { transform: translate(-10%,5%) }
  60% { transform: translate(15%,0) }
  70% { transform: translate(0,10%) }
  80% { transform: translate(-15%,0) }
  90% { transform: translate(10%,5%) }
  100% { transform: translate(5%,0) }
`;

const Noise = styled.div`
  position: absolute;
  inset: -200%;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  opacity: 0.03;
  animation: ${noise} 0.2s infinite;
  pointer-events: none;
  z-index: 4;
`;

const Img = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  object-fit: contain;
  opacity: 0.8;
  pointer-events: none;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${mirror};
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
  z-index: 3;
  opacity: 0.4;
`;

const FocusRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 280px;
  height: 280px;
  margin-top: -140px;
  margin-left: -140px;
  border: 2px dashed rgba(16, 185, 129, 0.5);
  border-radius: 50%;
  animation: ${rotate} 10s linear infinite;
  z-index: 5;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: -10px;
    border: 2px solid rgba(16, 185, 129, 0.2);
    border-radius: 50%;
    border-top-color: #10b981;
    animation: ${rotate} 3s linear infinite reverse;
  }
`;

const Telemetry = styled.div<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 80px;
  ${({ side }) => side === 'left' ? 'left: 30px;' : 'right: 30px; text-align: right;'}
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 10px;
  color: #10b981;
  opacity: 0.6;
  z-index: 6;
  pointer-events: none;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StatusLabel = styled.div`
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 100px;
  color: #10b981;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  z-index: 10;
  animation: ${blink} 2s infinite ease-in-out;
`;

const CornerBracket = styled.div<{ position: 'tl' | 'tr' | 'bl' | 'br' }>`
  position: absolute;
  width: 50px;
  height: 50px;
  border: 2px solid #10b981;
  filter: drop-shadow(0 0 5px #10b981);
  pointer-events: none;
  z-index: 6;

  ${({ position }) => {
    switch (position) {
      case 'tl': return 'top: 20px; left: 20px; border-right: none; border-bottom: none; border-radius: 8px 0 0 0;';
      case 'tr': return 'top: 20px; right: 20px; border-left: none; border-bottom: none; border-radius: 0 8px 0 0;';
      case 'bl': return 'bottom: 20px; left: 20px; border-right: none; border-top: none; border-radius: 0 0 0 8px;';
      case 'br': return 'bottom: 20px; right: 20px; border-left: none; border-top: none; border-radius: 0 0 8px 0;';
    }
  }}
`;

const ScanLine = styled.div`
  position: absolute;
  width: 100%;
  height: 2px;
  background: #10b981;
  box-shadow: 
    0 0 20px 2px #10b981,
    0 0 60px 15px rgba(16, 185, 129, 0.2);
  animation: ${scanAnimation} 3s ease-in-out infinite;
  z-index: 7;
  pointer-events: none;
`;



const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  gap: 1.5rem;
  align-items: center;
`;

const InfoBarWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: center;
  z-index: 10;
  margin-bottom: -1rem;
`;

const StatsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 30px;
  z-index: 10;
`;


const SuccessOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(20px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: white;
  animation: fadeIn 0.5s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SuccessTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SuccessSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 400px;
`;

const ScanSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 1rem 0 0;
  line-height: 1.6;
  font-weight: 500;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  ${media.tablet`
    font-size: 1.25rem;
  `}
`;

const ResetButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 1.25rem 2.5rem;
  border-radius: 100px;
  font-weight: 800;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 15px 30px rgba(16, 185, 129, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StatusSpinnerCircle = styled.div<{ $isSuccess: boolean }>`
  width: 48px;
  height: 48px;
  border: 4px solid ${({ $isSuccess }) => ($isSuccess ? '#10b981' : '#ef4444')};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const DesktopOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #5a6fc5;
  z-index: 2147483647;
  padding: 2rem;
  box-sizing: border-box;
`;

const DesktopCard = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 4rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
`;

const DeviceIconWrapper = styled.div`
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const DesktopTitle = styled.h2`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const DesktopSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.15rem;
  line-height: 1.6;
  max-width: 400px;
  font-weight: 500;
`;

const BiosenseSignalMonitor = ({
  showMonitor,
  cameraId,
  onLicenseStatus,
  onSettingsClick,
  isSettingsOpen,
}) => {
  const video = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [decodeVitalByPatientId] = useDecodeVitalByPatientIdMutation();
  const [updateLicenseUsageSession] = useUpdateLicenseUsageSessionMutation();
  const [getWebhookPatientData] = useLazyGetWebhookPatientDataQuery();
  const [sendWebhook] = useSendWebhookMutation();
  const isPageVisible = usePageVisibility();
  const isMediaTablet = useMediaPredicate('(min-width: 1000px)');
  const [processingTime] = useMeasurementDuration();
  const [licenseKey] = useLicenseKey();

  const [isMeasurementEnabled, setIsMeasurementEnabled] = useState<boolean>(
    false,
  );
  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isScanComplete, setIsScanComplete] = useState<boolean>(false);
  const [capturedResults, setCapturedResults] = useState<any>(null);
  const [showRawResponse, setShowRawResponse] = useState<boolean>(false);
  const [scanStatus, setScanStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<number>();
  const errorWebhookSentRef = useRef<boolean>(false);
  const state = location.state as any;

  const userInformation = useMemo(() => {
    if (!state?.isEnhanced) return null;

    return {
      sex: state.gender === 'male' ? Sex.MALE : state.gender === 'female' ? Sex.FEMALE : Sex.UNSPECIFIED,
      age: parseInt(state.age),
      weight: parseInt(state.weight),
      height: parseInt(state.height),
      smokingStatus: state.smoking === 'smoker' ? SmokingStatus.SMOKER : state.smoking === 'non-smoker' ? SmokingStatus.NON_SMOKER : SmokingStatus.UNSPECIFIED,
    };
  }, [state]);

  const {
    sessionState,
    vitalSigns,
    offlineMeasurements,
    error,
    warning,
    info,
    finalResults,
  } = useMonitor(
    video,
    cameraId,
    processingTime,
    licenseKey,
    null,
    startMeasuring,
    userInformation,
  );
  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  const warningMessage = useWarning(warning);
  const errorDetails = error?.code ? BIOSENSE_ERROR_MAP[error.code] : null;

  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState],
  );

  const isVideoReady = useCallback(
    () => video.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
    [],
  );

  const handleButtonClick = useCallback(async () => {
    console.log('start button');
    if (isScanComplete) {
      setIsScanComplete(false);
      setStartMeasuring(false);
      return;
    }
    setIsLoading(true);
    errorWebhookSentRef.current = false;

    if (sessionState === SessionState.ACTIVE) {
      try {
        const licenseUsageId = localStorage.getItem(StorageKeys.LICENSE_USAGE_ID) || localStorage.getItem('LICENSE_USAGE_ID');
        const patientId = localStorage.getItem(StorageKeys.PATIENT_ID);
        const orgId = localStorage.getItem(StorageKeys.ORG_ID);

        // Allow proceeding even without IDs, using default "GUEST" if necessary for the session update
        if (licenseUsageId) {
          await updateLicenseUsageSession({
            id: licenseUsageId,
            patient_id: patientId || 'GUEST',
            session_status: 1,
          }).unwrap();
          console.log('License usage session updated (GUEST fallback used if ID missing)');
        }
      } catch (err) {
        console.error('Error updating license usage session:', err);
      }
      setStartMeasuring(true);
      setLoadingTimeoutPromise(
        window.setTimeout(() => setIsLoading(true), processingTime * 1000),
      );
    } else if (isMeasuring()) {
      clearTimeout(loadingTimeoutPromise);
      window.location.reload();
    }
  }, [sessionState, setIsLoading, processingTime, isScanComplete, isMeasuring, loadingTimeoutPromise, updateLicenseUsageSession]);



  useEffect(() => {
    if (finalResults && (!finalResults.results || Object.keys(finalResults.results).length === 0)) {
        console.log("Empty results returned. Sending error webhook...");
        setScanStatus('error');
        setIsScanComplete(true);
        
        (async () => {
            try {
                let callbackUrl = WEBHOOK_URL;
                let clientTransactionId;
                let referralId;
                let token = localStorage.getItem('token');
                const deviceUserId = localStorage.getItem(StorageKeys.DEVICE_USER_ID);

                if (deviceUserId && token) {
                    const { data: patientJson } = await getWebhookPatientData({ deviceUserId, token });
                    if (patientJson && patientJson.success && patientJson.data) {
                        clientTransactionId = patientJson.data.client_transaction_id;
                        referralId = patientJson.data.referral_id;
                        callbackUrl = patientJson.data.call_back_URL || WEBHOOK_URL;
                    }
                }

                const payload: any = {
                    status: 'error',
                    event: 'SCAN_FAILED',
                    device_user_id: deviceUserId || 'GUEST',
                    results: {
                        error_message: 'Assessment failed',
                        error_code: 0
                    },
                    timestamp: new Date().toISOString(),
                };

                if (clientTransactionId) payload.client_transaction_id = clientTransactionId;
                if (referralId) payload.referral_id = referralId;
                if (token) payload.token = token;

                await sendWebhook({ call_back_URL: callbackUrl, payload }).unwrap();
                console.log("Webhook empty result error notification sent via backend");
            } catch (err) {
                console.error("Webhook empty result notification failed:", err);
            }
        })();
        return;
    }

    if (!finalResults) {
      return;
    }

    console.log("FINAL DATA READY", finalResults.results);
    setCapturedResults(vitalSigns);

    const patientId = localStorage.getItem(StorageKeys.PATIENT_ID);
    const deviceUserId = localStorage.getItem(StorageKeys.DEVICE_USER_ID);
    const idToUse = patientId || deviceUserId || 'GUEST';

    const encodedData = btoa(JSON.stringify(finalResults.results));

    // Commented out as per user request to use webhook instead
    /*
    decodeVitalByPatientId({
      patientId: idToUse,
      encodedData
    })
      .unwrap()
      .then((res) => {
        console.log("Decode Vital success:", res);
        setScanStatus('success');
        setIsScanComplete(true);

        // Broadcast success
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'BEAIVE_SCAN_RESULTS', status: 'success', data: finalResults.results }, '*');
        }
        if (window.opener) {
          window.opener.postMessage({ type: 'BEAIVE_SCAN_RESULTS', status: 'success', data: finalResults.results }, '*');
        }
      })
      .catch((err) => {
        console.error("Decode Vital failed:", err);
        setScanStatus('idle');
        setIsScanComplete(false);
        setStartMeasuring(false);
        setIsLoading(false);

        // Broadcast failure
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'BEAIVE_SCAN_RESULTS', status: 'error', data: finalResults.results, error: err }, '*');
        }
      });
    */

    // Use webhook directly instead of decodeVitalByPatientId
    console.log("Using Webhook for data reporting...");
    setScanStatus('success');
    setIsScanComplete(true);

    // Broadcast success
    // if (window.parent && window.parent !== window) {
    //   window.parent.postMessage({ type: 'BEAIVE_SCAN_RESULTS', status: 'success', data: finalResults.results }, '*');
    // }
    // if (window.opener) {
    //   window.opener.postMessage({ type: 'BEAIVE_SCAN_RESULTS', status: 'success', data: finalResults.results }, '*');
    // }

    // Send data to webhook
    // Local 
    // fetch('https://webhook.site/517dff31-7473-4e96-ae4b-add5036c69e5', {
    //   method: 'POST',
    //   mode: 'no-cors',
    //   headers: { 
    //     'Content-Type': 'text/plain',
    //   },
    //   body: JSON.stringify({
    //     status: 'success', 
    //     event: 'SCAN_COMPLETED',              
    //     results: finalResults.results,
    //     timestamp: new Date().toISOString(),
    //      //  patientId: idToUse,
    //     // vitalSigns: vitalSigns
    //   }),
    // })
    //   .then(() => console.log("Webhook notification sent successfully"))
    //   .catch(err => console.error("Webhook notification failed:", err));
    // server 
    (async () => {
        try {
            let callbackUrl = WEBHOOK_URL;
            let clientTransactionId;
            let referralId;
            let token = localStorage.getItem('token');

            if (deviceUserId && token) {
                const { data: patientJson } = await getWebhookPatientData({ deviceUserId, token });
                if (patientJson && patientJson.success && patientJson.data) {
                    clientTransactionId = patientJson.data.client_transaction_id;
                    referralId = patientJson.data.referral_id;
                    callbackUrl = patientJson.data.call_back_URL || WEBHOOK_URL;
                }
            }

            const payload: any = {
                status: 'success',
                event: 'SCAN_COMPLETED',
                device_user_id: deviceUserId || 'GUEST',
                results: finalResults.results,
                timestamp: new Date().toISOString(),
            };

            if (clientTransactionId) payload.client_transaction_id = clientTransactionId;
            if (referralId) payload.referral_id = referralId;
            if (token) payload.token = token;

            await sendWebhook({ call_back_URL: callbackUrl, payload }).unwrap();

            console.log("Webhook notification sent via backend");
        } catch (err) {
            console.error("Webhook notification failed:", err);
        }
    })();

  }, [finalResults]);


  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      setIsScanComplete(false);
      setScanStatus('idle');
      setCapturedResults(null);
      setShowRawResponse(false);

      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage && !errorWebhookSentRef.current
    ) {
      setIsMeasurementEnabled(false);
      errorWebhookSentRef.current = true;
      
      (async () => {
          try {
              let callbackUrl = WEBHOOK_URL;
              let clientTransactionId;
              let referralId;
              let token = localStorage.getItem('token');
              const deviceUserId = localStorage.getItem(StorageKeys.DEVICE_USER_ID);

              if (deviceUserId && token) {
                  const { data: patientJson } = await getWebhookPatientData({ deviceUserId, token });
                  if (patientJson && patientJson.success && patientJson.data) {
                      clientTransactionId = patientJson.data.client_transaction_id;
                      referralId = patientJson.data.referral_id;
                      callbackUrl = patientJson.data.call_back_URL || WEBHOOK_URL;
                  }
              }

              const payload: any = {
                  status: 'error',
                  event: 'SCAN_FAILED',
                  device_user_id: deviceUserId || 'GUEST',
                  results: {
                      error_message: errorMessage || 'Assessment failed',
                      error_code: error?.code || 0
                  },
                  timestamp: new Date().toISOString(),
              };

                  if (clientTransactionId) payload.client_transaction_id = clientTransactionId;
                  if (referralId) payload.referral_id = referralId;
                  if (token) payload.token = token;

                  await sendWebhook({ call_back_URL: callbackUrl, payload }).unwrap();

                  console.log("Webhook error notification sent via backend");
              } catch (err) {
                  console.error("Webhook error notification failed:", err);
              }
          })();
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
  }, [errorMessage, sessionState, isPageVisible, prevSessionState, error, isMeasuring, getWebhookPatientData]);

  useEffect(() => {
    onLicenseStatus(!(error?.code in HealthMonitorCodes));
  }, [error]);


  const mobile = useMemo(() => isMobile(), []);
  
  const [desktop, setDesktop] = useState(() => !isTablet() && !isMobile());

  useEffect(() => {
    const handleResize = () => {
      setDesktop(!isTablet() && !isMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const currentMask = useMemo(() => {
    if (errorMessage || info.message) return MaskRed; // Show red mask on error OR warning (validity message)
    if (sessionState === SessionState.MEASURING) return MaskGreen;
    return Mask;
  }, [errorMessage, sessionState, info.message]);

  if (desktop) {
    return createPortal(
      <DesktopOverlay>
        <DesktopCard>
          <DeviceIconWrapper>
            <svg width="50" height="72" viewBox="0 0 24 36" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M4 0C1.79086 0 0 1.79086 0 4V32C0 34.2091 1.79086 36 4 36H20C22.2091 36 24 34.2091 24 32V4C24 1.79086 22.2091 0 20 0H4ZM2 5H22V29H2V5Z" fill="white"/>
            </svg>
          </DeviceIconWrapper>
          <DesktopTitle>Device Not Supported</DesktopTitle>
          <DesktopSubtitle>
            We suggest using a mobile or tablet device to enjoy the best experience.
          </DesktopSubtitle>
        </DesktopCard>
      </DesktopOverlay>,
      document.body
    );
  }

  if (!showMonitor || (isScanComplete && scanStatus === 'success')) {
    //return null;
    return <ScanSubtitle>{scanStatus === 'success' ? 'Assessment Completed' : 'Assessment Failed'}</ScanSubtitle>;
  }

  return (
    <MonitorWrapper>
      <InfoBarWrapper>
        <InfoBar
          showTimer={isMeasurementEnabled && !isMediaTablet}
          isMeasuring={isMeasuring()}
          durationSeconds={processingTime}
          offlineMeasurements={offlineMeasurements}
        />
      </InfoBarWrapper>

      <VideoAndStatsWrapper isMeasuring={isMeasuring()}>

        <VideoWrapper>
          <Img src={currentMask} />
          <Video ref={video} id="video" muted={true} playsInline={true} />
          <ScanlineOverlay />
          <Noise />
        </VideoWrapper>

        {isMeasuring() && !errorMessage && (
          <>
            <StatusLabel>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 10px #10b981' }} />
              Live Analysis Active
            </StatusLabel>
            <FocusRing />
            <Telemetry side="left">
              Signal: Stable<br />
              FPS: 60.0<br />
              Scan: {Math.floor(Math.random() * 100)}%<br />
              rPPG: Active
            </Telemetry>
            <Telemetry side="right">
              SESSION: SCAN-{Math.floor(Math.random() * 900) + 100}<br />
              LOC: {Number(location.pathname.length).toFixed(4)}, {Number(Date.now().toString().slice(-4)).toFixed(4)}<br />
              ID: BS-{Math.floor(Math.random() * 900) + 100}<br />
              ENC: AES-256
            </Telemetry>
            <ScanLine />
            <GridOverlay />
            <CornerBracket position="tl" />
            <CornerBracket position="tr" />
            <CornerBracket position="bl" />
            <CornerBracket position="br" />
          </>
        )}

        {isScanComplete && (
          <SuccessOverlay>
            <div style={{
              background: scanStatus === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              padding: '24px',
              borderRadius: '50%',
              marginBottom: '20px',
              border: `1px solid ${scanStatus === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`
            }}>
              <StatusSpinnerCircle $isSuccess={scanStatus === 'success'} />
            </div>

            <div style={{
              background: scanStatus === 'success' ? '#10b981' : '#ef4444',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '900',
              letterSpacing: '1px',
              marginBottom: '1rem',
              textTransform: 'uppercase'
            }}>
              {scanStatus === 'success' ? 'Status: Success' : 'Status: Failed'}
            </div>

            <SuccessTitle>{scanStatus === 'success' ? 'Assessment Completed' : 'Assessment Failed'}</SuccessTitle>
            <SuccessSubtitle>
              {scanStatus === 'success'
                ? 'Your health assessment is complete. Diagnostics have been successfully encrypted.'
                : 'There was an error processing the diagnostic data. Please try again.'
              }
            </SuccessSubtitle>

            <div style={{ width: '100%', maxWidth: '90%', marginBottom: '1.5rem', maxHeight: '180px', overflowY: 'auto' }}>
              {showRawResponse ? (
                <pre style={{
                  textAlign: 'left',
                  background: 'rgba(0,0,0,0.5)',
                  padding: '1rem',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  color: scanStatus === 'success' ? '#10b981' : '#ef4444',
                  border: `1px solid ${scanStatus === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  {JSON.stringify({
                    status: scanStatus,
                    timestamp: Date.now(),
                    patientId: localStorage.getItem(StorageKeys.PATIENT_ID) || 'GUEST',
                    results: capturedResults || {}
                  }, null, 2)}
                </pre>
              ) : (
                <div style={{ opacity: scanStatus === 'success' ? 1 : 0.5 }}>
                  <Stats vitalSigns={capturedResults || vitalSigns} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <ResetButton onClick={() => {
                  setIsScanComplete(false);
                  setStartMeasuring(false);
                }}>
                  {scanStatus === 'success' ? 'New Analysis' : 'Try Again'}
                </ResetButton>


              </div>

              {localStorage.getItem('returnUrl') && (
                <button
                  onClick={() => {
                    const url = localStorage.getItem('returnUrl')!;
                    const responseObj = {
                      status: scanStatus,
                      data: capturedResults || {}
                    };
                    console.log("responseObj", responseObj)
                    const encodedResults = btoa(JSON.stringify(responseObj));
                    const separator = url.includes('?') ? '&' : '?';
                    window.location.href = `${url}${separator}response=${encodedResults}`;
                  }}
                  style={{
                    background: 'transparent',
                    color: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '100px',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Confirm & Return
                </button>
              )}
            </div>
          </SuccessOverlay>
        )}





        {!isVideoReady() && licenseKey && <Loader />}


        {(isMeasuring() ? !errorMessage && !warningMessage : !errorMessage) &&
          isMeasurementEnabled && (
            <StatsContainer>
              <Stats vitalSigns={vitalSigns} />
            </StatsContainer>
          )}
      </VideoAndStatsWrapper>

      <ControlsContainer>
        <ErrorAlert message={errorMessage} solution={errorDetails?.solution} />
        {isMeasuring() && <WarningAlert message={warningMessage} />}
        {isMeasuring() && <InfoAlert message={info.message} />}

        <StartButton
          isLoading={isLoading}
          isMeasuring={isMeasuring()}
          onClick={handleButtonClick}
        />

      </ControlsContainer>
    </MonitorWrapper>
  );
};


export default BiosenseSignalMonitor;
